import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Header = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Dynamische Header-Höhe für Hero/Seite (alle Geräte)
  useEffect(() => {
    const setHeaderHeight = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          "--header-height",
          `${headerRef.current.offsetHeight}px`
        );
      }
    };
    setHeaderHeight();
    const ro = new ResizeObserver(setHeaderHeight);
    const el = headerRef.current;
    if (el) ro.observe(el);
    window.addEventListener("resize", setHeaderHeight);
    return () => {
      window.removeEventListener("resize", setHeaderHeight);
      if (el) ro.unobserve(el);
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.history.replaceState(null, "", `#${sectionId}`);
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleNavClick = (e, sectionId) => {
    setIsMobileMenuOpen(false);
    if (isHome) {
      e.preventDefault();
      // Kurz warten, bis Mobile-Menü zu ist (Layout aktualisiert), dann springen – wichtig für korrekte Position auf Smartphone
      const delay = window.innerWidth < 768 ? 150 : 0;
      setTimeout(() => scrollToSection(sectionId), delay);
    }
    // Sonst: Link zu /#sectionId lässt React Router navigieren, Home scrollt per useEffect
  };

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 header-bar ${
        isScrolled ? "header-bar--scrolled shadow-lg" : "header-bar--top"
      }`}
    >
      <div className="container-custom">
        <div className="header-inner flex items-center justify-between">
          {/* Logo – auf hellem Grund für optimale Lesbarkeit (#081d3f) */}
          <Link
            to="/"
            onClick={(e) => isHome && (e.preventDefault(), scrollToSection("hero"))}
            className="flex items-center hover:opacity-90 transition-opacity focus:outline-none"
            aria-label="Zur Startseite"
          >
            <img
              src={`${process.env.PUBLIC_URL || ""}/logo.svg`}
              alt="Marmoris"
              className="header-logo-img h-10 w-auto md:h-12 transition-[height] duration-300"
            />
          </Link>

          {/* Desktop Navigation – Farben abgestimmt auf Logo #081d3f */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/#leistungen"
              onClick={(e) => handleNavClick(e, "leistungen")}
              className="header-nav-link text-sm font-medium relative group"
            >
              Leistungen
              <span className="header-nav-underline group-hover:w-full"></span>
            </Link>
            <Link
              to="/#referenzen"
              onClick={(e) => handleNavClick(e, "referenzen")}
              className="header-nav-link text-sm font-medium relative group"
            >
              Referenzen
              <span className="header-nav-underline group-hover:w-full"></span>
            </Link>
            <Link
              to="/#faq"
              onClick={(e) => handleNavClick(e, "faq")}
              className="header-nav-link text-sm font-medium relative group"
            >
              FAQ
              <span className="header-nav-underline group-hover:w-full"></span>
            </Link>
            <Link
              to="/#kontakt"
              onClick={(e) => handleNavClick(e, "kontakt")}
              className="px-6 py-3 bg-[#081d3f] text-white text-sm font-medium rounded-lg hover:bg-[#0d2847] shadow-md hover:shadow-lg transition-all duration-300 inline-block"
            >
              Kontakt
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#081d3f] p-2 hover:bg-[#081d3f]/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation – gleicher heller Stil wie Header */}
        {isMobileMenuOpen && (
          <nav className="md:hidden pb-6 space-y-4 header-mobile-menu rounded-b-2xl">
            <Link
              to="/#leistungen"
              onClick={(e) => handleNavClick(e, "leistungen")}
              className="block w-full text-left px-4 py-3 text-[#081d3f]/90 hover:text-[#081d3f] hover:bg-[#081d3f]/5 rounded-lg transition-colors font-medium"
            >
              Leistungen
            </Link>
            <Link
              to="/#referenzen"
              onClick={(e) => handleNavClick(e, "referenzen")}
              className="block w-full text-left px-4 py-3 text-[#081d3f]/90 hover:text-[#081d3f] hover:bg-[#081d3f]/5 rounded-lg transition-colors font-medium"
            >
              Referenzen
            </Link>
            <Link
              to="/#faq"
              onClick={(e) => handleNavClick(e, "faq")}
              className="block w-full text-left px-4 py-3 text-[#081d3f]/90 hover:text-[#081d3f] hover:bg-[#081d3f]/5 rounded-lg transition-colors font-medium"
            >
              FAQ
            </Link>
            <Link
              to="/#kontakt"
              onClick={(e) => handleNavClick(e, "kontakt")}
              className="block w-full text-left px-4 py-3 text-white font-medium bg-[#081d3f] hover:bg-[#0d2847] rounded-lg transition-colors"
            >
              Kontakt
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
